import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons'

interface ManageReservationProps {
  reservation: {
    cancellationPolicy: string
  }
}

const CLOSE_DELAY_MS = 150

const CancellationPolicy = ({reservation}: ManageReservationProps) => {
  const [showDropdown, setShowDropdown] = useState(false)
  let closeTimer: number | null = null

  // Helper function to strip HTML tags from cancellation policy
  const stripHtmlTags = (html: string): string => {
    if (!html) {
      return ''
    }

    // Replace <br> tags with newlines
    let text = html.replace(/<br\s*\/?>/gi, '\n')

    // Replace </p> and </div> with double newlines for paragraph separation
    text = text.replace(/<\/(p|div)>/gi, '\n\n')

    // Remove all other HTML tags
    text = text.replace(/<[^>]*>/g, '')

    // Decode HTML entities
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    text = textarea.value

    // Clean up excessive whitespace while preserving intentional line breaks
    text = text.replace(/\n\s*\n\s*\n/g, '\n\n') // Max 2 consecutive newlines
    text = text.trim()

    return text
  }
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{top: number; left: number}>({
    top: 0,
    left: 0
  })

  const clearCloseTimer = () => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer)
      closeTimer = null
    }
  }

  const startCloseTimer = () => {
    clearCloseTimer()
    closeTimer = window.setTimeout(() => {
      setShowDropdown(false)
    }, CLOSE_DELAY_MS)
  }

  const updatePosition = () => {
    const trigger = triggerRef.current
    if (!trigger) {
      return
    }
    const rect = trigger.getBoundingClientRect()
    const offset = 8
    const popupWidth = 340
    const maxLeft = Math.max(offset, window.innerWidth - popupWidth - offset)
    const clampedLeft = Math.min(Math.max(rect.left, offset), maxLeft)
    setPosition({top: rect.bottom + offset, left: clampedLeft})
  }

  useLayoutEffect(() => {
    if (!showDropdown) {
      return
    }
    updatePosition()
  }, [showDropdown])

  useEffect(() => {
    if (!showDropdown) {
      return
    }
    const onScrollOrResize = () => updatePosition()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [showDropdown])

  return (
    <div
      ref={triggerRef}
      className="inline-block"
      onMouseEnter={() => {
        clearCloseTimer()
        setShowDropdown(true)
        updatePosition()
      }}
      onMouseLeave={() => startCloseTimer()}
    >
      <div
        className="flex items-center gap-1 cursor-pointer"
        onClick={e => {
          e.stopPropagation()
          setShowDropdown(prev => {
            const next = !prev
            if (!prev) {
              updatePosition()
            }
            return next
          })
        }}
        tabIndex={0}
        aria-label="Show cancellation policy"
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation()
            setShowDropdown(prev => {
              const next = !prev
              if (!prev) {
                updatePosition()
              }
              return next
            })
          }
        }}
      >
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="text-cabana text-sm mr-1"
          title="Cancellation policy info"
        />
        <a
          className="text-sm font-normal no-underline hover:underline text-[#455966]"
          tabIndex={-1}
        >
          Cancellation policy
        </a>
      </div>
      {showDropdown && (
        <div
          ref={popoverRef}
          className="card compact bg-white border rounded-box z-50 shadow-lg fixed w-[340px]"
          style={{top: position.top, left: position.left}}
          role="dialog"
          aria-label="Cancellation policy dialog"
          onMouseEnter={() => clearCloseTimer()}
          onMouseLeave={() => startCloseTimer()}
        >
          <div className="card-body">
            <div className="font-semibold text-base border-b mb-2">
              Cancellation Policy
            </div>
            <div className="text-sm max-h-64 overflow-y-auto pr-1 whitespace-pre-wrap">
              {stripHtmlTags(reservation.cancellationPolicy)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CancellationPolicy
